import type { CompanySearchResult } from '@dash-lite/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Page } from '@/components/layout/page';
import { Section } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { getCompanyWithMeta } from '@/lib/dashlite-envelope';
import type { CompanyEnvelope } from '@/lib/dashlite-envelope';
import { isApiError } from '@/lib/errors';
import { paths } from '@/lib/paths';
import { jsPDF } from 'jspdf';

type CompanyData = CompanyEnvelope['data'];
type CompanyOfficer = NonNullable<CompanyData['officers']>[number];

/**
 * Fiche d'une entreprise — l'écran qui matérialise la règle métier centrale
 * de la plateforme : **aucune fiche complète sans droit d'accès actif**.
 *
 * Trois réponses possibles, trois écrans :
 *
 * | Réponse de l'API                | Ce que voit le visiteur                    |
 * | ------------------------------- | ------------------------------------------ |
 * | `200` + `{ data, meta }`        | la fiche complète (+ bandeau si `stale`)   |
 * | `401 unauthenticated`           | l'aperçu verrouillé et l'offre du site     |
 * | `403 entitlement_required`      | idem — il a un compte, pas encore d'accès  |
 * | `404 company_not_found`         | « entreprise introuvable »                 |
 * | `503 upstream_unavailable`      | panne temporaire, avec un bouton réessayer |
 *
 * Le mur payant n'est donc pas décidé côté front : il est la conséquence
 * d'une réponse de l'API. C'est ce qui garantit qu'aucune donnée protégée
 * n'a transité vers le navigateur.
 */
export default function CompanyPage() {
    const { registrationNumber = '' } = useParams();
    const location = useLocation();
    const { hasAccess } = useSession();


    // Si pas d'accès actif, redirection automatique vers la page de paiement de cette entreprise
    if (!hasAccess && registrationNumber) {
        return <Navigate to={paths.buy(registrationNumber)} replace />;
    }

    /**
     * Aperçu éventuel transporté depuis la liste de résultats : il permet
     * d'afficher le nom de l'entreprise sans attendre — et même lorsque la
     * fiche complète est refusée.
     */
    const preview = (location.state as { preview?: CompanySearchResult } | null)?.preview ?? null;

    const [envelope, setEnvelope] = useState<CompanyEnvelope | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            console.log('🔍 registrationNumber:', registrationNumber);

            const result = await getCompanyWithMeta(registrationNumber);

            console.log('📦 API response:', result);

            setEnvelope(result);
        } catch (caught) {

            console.error('❌ API ERROR:', caught);

            setEnvelope(null);
            setError(caught);
        } finally {
            setLoading(false);
        }
    }, [registrationNumber]);

    /**
     * Chargement de la fiche, une fois par identifiant.
     *
     * L'identifiant déjà chargé est mémorisé dans une référence, ce qui
     * neutralise le double montage de `StrictMode` en développement : sans
     * cela, chaque affichage produirait deux appels identiques.
     */
    const loadedNumber = useRef<string | null>(null);

    useEffect(() => {
        if (loadedNumber.current === registrationNumber) {
            return;
        }

        loadedNumber.current = registrationNumber;
        void load();
    }, [load, registrationNumber]);

    /**
     * Réouverture après achat.
     *
     * Quand le droit d'accès s'ouvre — au retour du tunnel de paiement, ou
     * après une connexion — on rejoue l'appel qui avait été refusé. La
     * condition porte sur l'erreur précédente, pas sur `hasAccess` seul :
     * sans cela, la simple arrivée de `GET /me` déclencherait un second
     * appel identique alors que la fiche est déjà à l'écran.
     */
    useEffect(() => {
        if (hasAccess && isApiError(error, 'entitlement_required', 'unauthenticated')) {
            void load();
        }
    }, [hasAccess, error, load]);

    const title = envelope?.data.name ?? preview?.name ?? registrationNumber;

    return (
        <Page title={title}>
            
            <Section className="py-0 pb-16">
                <div className="mx-auto max-w-6xl px-4 md:px-6">
                    {/* Wrapper: stacks on mobile, row on desktop */}
                    <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">

                        {/* En-tête de section (left) */}
                        <div>
                            <div className="mb-2 inline-flex items-center rounded-full border border-green-200 bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600"></span>
                                Document disponible
                            </div>
                            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                                Votre Fiche d'Identité Entreprise
                            </h1>
                            <p className="mt-1 text-[var(--muted-foreground)]">
                                SIREN :{' '}
                                <span className="font-mono font-medium text-[var(--foreground)]">
                                    {registrationNumber}
                                </span>
                            </p>
                        </div>

                        {/* Actions Principales (right) */}
                        <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row">

                            {envelope && (
                                <>
                                    <DownloadSwissExtractButton
                                        company={envelope.data}
                                    />

                                    <PreviewSwissExtractButton
                                        company={envelope.data}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Layout Contenu */}
                    <div className="grid gap-8 lg:grid-cols-3">
                        {/* Colonne Principale : Aperçu */}
                        <div className="lg:col-span-2">
                            <div className="relative w-full overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--secondary)] p-4 shadow-sm md:p-8">
                                <div className="mx-auto max-w-[600px] bg-white shadow-md ring-1 ring-black/5 transition-transform md:hover:scale-[1.01]">
                                    {/* Image Preview */}
                                    <img
                                        className="h-auto w-full object-contain"
                                        src={'/images/specimen-document.png'}
                                        alt="Aperçu document"
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Sidebar : Informations */}
                        <div className="flex flex-col gap-6">
                            <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--card)] p-6 text-[var(--card-foreground)] shadow-sm">
                                <h2 className="mb-4 text-sm font-semibold tracking-wider text-[var(--muted-foreground)] uppercase">
                                    Informations
                                </h2>
                                <dl className="space-y-4 text-sm">
                                    <div className="flex justify-between border-b border-[var(--border)] pb-4 last:border-0 last:pb-0">
                                        <dt className="text-[var(--muted-foreground)]">Type</dt>
                                        <dd className="font-medium">Fiche d'identité entreprise</dd>
                                    </div>
                                    <div className="flex justify-between border-b border-[var(--border)] pb-4 last:border-0 last:pb-0">
                                        <dt className="text-[var(--muted-foreground)]">Format</dt>
                                        <dd className="font-medium">PDF Numérique</dd>
                                    </div>
                                    <div className="flex justify-between border-b border-[var(--border)] pb-4 last:border-0 last:pb-0">
                                        <dt className="text-[var(--muted-foreground)]">Délivré le</dt>
                                        <dd className="font-medium">
                                            {new Date().toLocaleDateString('fr-FR')}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>
        </Page>
    );
}





function createSwissExtractPdf(company: CompanyData) {
    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const safeText = (value: unknown): string => {
        if (
            value === null ||
            value === undefined ||
            value === ''
        ) {
            return 'Non communiqué';
        }

        return String(value);
    };

    const formatDate = (
        value: string | null | undefined,
    ): string => {
        if (!value) {
            return 'Non communiqué';
        }

        const date = new Date(`${value}T00:00:00`);

        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const formatAddress = (): string => {
        const address = company.registry?.headquarters;

        if (!address) {
            return 'Non communiqué';
        }

        const city = [
            address.postal_code,
            address.city,
        ]
            .filter(Boolean)
            .join(' ');

        const country =
            address.country === 'CH'
                ? 'Suisse'
                : address.country;

        return [
            address.line1,
            address.line2,
            city,
            country,
        ]
            .filter(Boolean)
            .join(', ');
    };

    const formatStatus = (): string => {
        const status =
            company.registry?.registration_status ??
            company.status;

        if (!status) {
            return 'Non communiqué';
        }

        switch (String(status).toLowerCase()) {
            case 'active':
                return 'En activité';

            case 'inactive':
                return 'Inactive';

            case 'closed':
                return 'Fermée';

            case 'struck_off':
                return 'Radiée';

            default:
                return String(status);
        }
    };

    const formatActivity = (): string => {
        const code = company.registry?.activity?.code;
        const label = company.registry?.activity?.label;
        const description =
            company.registry?.activity?.description;

        const values = [
            code,
            label,
            description,
        ].filter(Boolean);

        return values.length
            ? values.join(' — ')
            : 'Non communiqué';
    };

    const formatWorkforce = (): string => {
        const workforce =
            company.registry?.workforce;

        if (!workforce) {
            return 'Non communiqué';
        }

        if (
            workforce.count !== null &&
            workforce.count !== undefined
        ) {
            return `${workforce.count} collaborateur${
                workforce.count > 1 ? 's' : ''
            }`;
        }

        if (workforce.range) {
            return workforce.range;
        }

        return 'Non communiqué';
    };

    // ==========================================
    // BORDURE
    // ==========================================

    pdf.setDrawColor(170);
    pdf.setLineWidth(0.25);

    pdf.rect(
        10,
        10,
        pageWidth - 20,
        pageHeight - 20,
    );

    // ==========================================
    // SOURCE
    // ==========================================

    pdf.setTextColor(0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);

    pdf.text(
        'Registre du commerce suisse',
        24,
        28,
    );

    pdf.text(
        'Source : Open Data RC',
        24,
        32,
    );

    // ==========================================
    // TITRE
    // ==========================================

    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);

    pdf.text(
        "EXTRAIT D'IMMATRICULATION (R.C.)",
        pageWidth / 2,
        51,
        {
            align: 'center',
        },
    );

    // ==========================================
    // SOUS-TITRE
    // ==========================================

    pdf.setFont('times', 'italic');
    pdf.setFontSize(8.5);

    pdf.text(
        "Identification de l'entité inscrite au Registre du commerce",
        pageWidth / 2,
        58,
        {
            align: 'center',
        },
    );

    pdf.setFont('times', 'normal');

    pdf.text(
        `à jour au ${formatDate(
            company.registry?.last_updated_at,
        )}`,
        pageWidth / 2,
        63,
        {
            align: 'center',
        },
    );

    // ==========================================
    // IDENTIFICATION
    // ==========================================

    const left = 24;
    const right = 186;

    pdf.setFont('times', 'bold');
    pdf.setFontSize(9.5);

    pdf.text(
        "IDENTIFICATION DE L'ENTITÉ",
        left,
        74,
    );

    pdf.setDrawColor(70);
    pdf.setLineWidth(0.2);

    pdf.line(left, 77, right, 77);

    // ==========================================
    // ROWS
    // ==========================================

    const labelX = 27;
    const valueX = 77;

    let y = 84;

    const rowHeight = 6.4;

    const drawRow = (
        label: string,
        value: unknown,
        bold = false,
    ) => {
        pdf.setFont('times', 'italic');
        pdf.setFontSize(8.5);

        pdf.text(label, labelX, y);

        pdf.setFont(
            'times',
            bold ? 'bold' : 'normal',
        );

        const finalValue = safeText(value);

        const lines = pdf.splitTextToSize(
            finalValue,
            right - valueX,
        );

        pdf.text(lines, valueX, y);

        const lineCount = Array.isArray(lines)
            ? lines.length
            : 1;

        y += Math.max(
            rowHeight,
            lineCount * 4.5,
        );
    };

    drawRow(
        'Raison sociale',
        company.name,
        true,
    );

    drawRow(
        "Numéro d'entreprise",
        company.registry
            ?.registration_number_formatted ??
            company.registration_number,
    );

    drawRow(
        'IDE / N° TVA',
        company.vat_number,
    );

    drawRow(
        'Statut',
        formatStatus(),
    );

    drawRow(
        "Date d'inscription",
        formatDate(
            company.registry?.registration_date ??
                company.incorporation_date,
        ),
    );

    drawRow(
        'Siège',
        formatAddress(),
    );

    drawRow(
        'Forme juridique',
        company.legal_form,
    );

    drawRow(
        'Activité',
        formatActivity(),
    );

    // ==========================================
    // DONNÉES ÉCONOMIQUES
    // ==========================================

    y += 5;

    pdf.setFont('times', 'bold');
    pdf.setFontSize(9.5);

    pdf.text(
        'DONNÉES ÉCONOMIQUES',
        left,
        y,
    );

    y += 3;

    pdf.line(left, y, right, y);

    y += 7;

    drawRow(
        'Capital',
        company.registry?.capital?.amount !== null &&
        company.registry?.capital?.amount !== undefined
            ? `${company.registry.capital.amount} ${
                  company.registry.capital.currency ?? ''
              }`.trim()
            : 'Non communiqué',
    );

    drawRow(
        'Effectifs',
        formatWorkforce(),
    );

    drawRow(
        "Date de début d'activité",
        formatDate(
            company.registry?.activity_start_date,
        ),
    );

    drawRow(
        "Nombre d'établissements",
        company.registry?.establishments_count,
    );

    drawRow(
        "Établissements ouverts",
        company.registry?.open_establishments_count,
    );

    // ==========================================
    // DIRIGEANTS
    // ==========================================

    if (
        Array.isArray(company.officers) &&
        company.officers.length > 0
    ) {
        y += 5;

        pdf.setFont('times', 'bold');
        pdf.setFontSize(9.5);

        pdf.text(
            'DIRIGEANTS',
            left,
            y,
        );

        y += 3;

        pdf.line(left, y, right, y);

        y += 7;

        company.officers.forEach((officer: CompanyOfficer) => {
            drawRow(
                officer.role ?? 'Fonction',
                officer.name,
            );
        });
    }

    // ==========================================
    // FOOTER
    // ==========================================

    y += 4;

    pdf.setDrawColor(170);
    pdf.setLineWidth(0.15);

    pdf.line(left, y, right, y);

    y += 6;

    pdf.setFont('times', 'italic');
    pdf.setFontSize(6.5);
    pdf.setTextColor(90);

    pdf.text(
        "Document privé à vocation purement informative — Données issues de l'Open Data RC (Non officiel)",
        pageWidth / 2,
        y,
        {
            align: 'center',
        },
    );

    return pdf;
}

function DownloadSwissExtractButton({
    company,
}: {
    company: CompanyData;
}) {
    const handleDownload = () => {
        const pdf =
            createSwissExtractPdf(company);

        const registrationNumber =
            company.registry
                ?.registration_number_formatted ??
            company.registration_number;

        pdf.save(
            `extrait-immatriculation-${registrationNumber}.pdf`,
        );
    };

    return (
        <button
            type="button"
            onClick={handleDownload}
            className="inline-flex items-center justify-center rounded-[var(--radius)] bg-[var(--primary)] px-5 py-2.5 text-sm font-medium text-[var(--primary-foreground)] shadow-sm transition-all hover:opacity-90 active:scale-[0.98]"
        >
            Télécharger le PDF
        </button>
    );
}

function PreviewSwissExtractButton({
    company,
}: {
    company: CompanyData;
}) {
    const handlePreview = () => {
        const pdf =
            createSwissExtractPdf(company);

        const blob = pdf.output('blob');

        const url =
            URL.createObjectURL(blob);

        window.open(
            url,
            '_blank',
            'noopener,noreferrer',
        );

        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 60000);
    };

    return (
        <button
            type="button"
            onClick={handlePreview}
            className="inline-flex items-center justify-center rounded-[var(--radius)] border border-[var(--border)] bg-[var(--background)] px-5 py-2.5 text-sm font-medium text-[var(--foreground)] transition-all hover:bg-[var(--secondary)] active:scale-[0.98]"
        >
            Aperçu PDF
        </button>
    );
}